






























import DatePicker from "@/shared/components/date-picker.vue";
import { required, validDate } from "@/shared/validators";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

export interface IntervaloPeriodo {
  dataInicio: string;
  dataFim: string;
}

@Component({
  components: {
    DatePicker,
  },
})
export default class PeriodoNavegavel extends Vue {
  @Prop() value!: IntervaloPeriodo;
  @Prop({ default: 31 }) maxDias!: number;

  intervalo: IntervaloPeriodo = {
    dataInicio: "",
    dataFim: "",
  };

  rules = {
    dataInicio: [required(), validDate(), () => this.erroIntervalo || true],
    dataFim: [required(), validDate(), () => this.erroIntervalo || true],
  };

  mounted() {
    if (this.value?.dataInicio && this.value?.dataFim) {
      this.intervalo = { ...this.value };
      return;
    }

    const hoje = dayjs();
    this.aplicar(hoje.startOf("month"), hoje.endOf("month"));
  }

  // O consumidor nao consegue deduzir a validade sozinho: quando o intervalo fica invalido
  // o `input` e suprimido, entao o v-model dele congela no ultimo valor valido e divergiria
  // do que esta na tela. Sem este aviso, um "Pesquisar" buscaria o periodo antigo enquanto
  // os campos mostram outro.
  @Watch("erroIntervalo", { immediate: true })
  onErroIntervaloChange(erro: string | null) {
    this.$emit("erro", erro);
  }

  get inicio(): Dayjs {
    return dayjs(this.intervalo.dataInicio);
  }

  get fim(): Dayjs {
    return dayjs(this.intervalo.dataFim);
  }

  get intervaloValido(): boolean {
    return this.inicio.isValid() && this.fim.isValid();
  }

  get ehMesInteiro(): boolean {
    return (
      this.intervaloValido &&
      this.inicio.isSame(this.inicio.startOf("month"), "day") &&
      this.fim.isSame(this.inicio.endOf("month"), "day")
    );
  }

  get ehMesmoDia(): boolean {
    return this.intervaloValido && this.inicio.isSame(this.fim, "day");
  }

  get larguraEmDias(): number {
    return this.fim.diff(this.inicio, "day") + 1;
  }

  get erroIntervalo(): string | null {
    if (!this.intervaloValido) {
      return null;
    }

    if (this.fim.isBefore(this.inicio, "day")) {
      return "A data final deve ser maior ou igual à inicial";
    }

    if (this.larguraEmDias > this.maxDias) {
      return `O período deve ter no máximo ${this.maxDias} dias`;
    }

    return null;
  }

  get rotulo(): string {
    if (!this.intervaloValido) {
      return "Selecione um período";
    }

    if (this.ehMesInteiro) {
      const mes = this.inicio.locale("pt-br").format("MMMM [de] YYYY");
      return mes.charAt(0).toUpperCase() + mes.slice(1);
    }

    if (this.ehMesmoDia) {
      return this.inicio.format("DD/MM/YYYY");
    }

    return `${this.inicio.format("DD/MM/YYYY")} - ${this.fim.format("DD/MM/YYYY")}`;
  }

  navegar(passo: number) {
    if (!this.intervaloValido) {
      return;
    }

    if (this.ehMesInteiro) {
      const inicio = this.inicio.add(passo, "month").startOf("month");
      this.aplicar(inicio, inicio.endOf("month"));
      return;
    }

    if (this.ehMesmoDia) {
      const inicio = this.inicio.add(passo, "day");
      this.aplicar(inicio, inicio);
      return;
    }

    const deslocamento = passo * this.larguraEmDias;
    this.aplicar(
      this.inicio.add(deslocamento, "day"),
      this.fim.add(deslocamento, "day"),
    );
  }

  aplicar(inicio: Dayjs, fim: Dayjs) {
    this.intervalo = {
      dataInicio: inicio.format("YYYY-MM-DD"),
      dataFim: fim.format("YYYY-MM-DD"),
    };
    this.emitir();
  }

  emitir() {
    // Editar so a data inicial deixa o intervalo transitoriamente invalido (ex.: 01/07/2025
    // com o fim ainda em 30/06/2026). Nao da pra confiar no form.validate() do consumidor
    // para barrar: o Vuetify so reavalia as rules no tick seguinte, entao a busca sairia com
    // o intervalo invalido e tomaria 400 do backend enquanto o usuario ainda esta digitando.
    if (this.erroIntervalo) {
      return;
    }

    this.$emit("input", { ...this.intervalo });
  }
}
